import type { TFunction } from 'i18next';
import type { ReactNode } from 'react';

import { errorMessage } from '@/api/client';
import type { TransferProgress } from '@/features/vault/transfer/exportVault';
import { TransferError, type SkippedEntry } from '@/features/vault/transfer/types';

/** Textul afișat pentru o eroare: codurile `TransferError` trec prin i18n. */
export function transferErrorText(err: unknown, t: TFunction): string {
  if (err instanceof TransferError) return t(`transfer.errors.${err.code}`, err.params);
  return errorMessage(err);
}

export function skippedReason(entry: SkippedEntry, t: TFunction): string {
  return t(`transfer.errors.${entry.code}`, entry.params ?? {});
}

export function ErrorBox({ children }: { children: ReactNode }) {
  return (
    <p className="text-sm" role="alert" style={{ color: 'var(--danger)' }}>
      {children}
    </p>
  );
}

export function ProgressLine({ progress }: { progress: TransferProgress | null }) {
  if (!progress) return null;
  const percent = progress.total > 0 ? Math.round((progress.done / progress.total) * 100) : 0;
  return (
    <div className="space-y-1.5" aria-live="polite">
      <div
        className="h-1.5 rounded-full overflow-hidden"
        style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)' }}
      >
        <div
          className="h-full transition-all"
          style={{ width: `${percent}%`, background: 'var(--accent)' }}
        />
      </div>
      <p className="text-xs text-[var(--text-muted)] truncate">
        {progress.done}/{progress.total}
        {progress.currentLabel ? ` — ${progress.currentLabel}` : ''}
      </p>
    </div>
  );
}

/** Listă derulabilă „titlu — motiv" pentru rapoartele de import și export. */
export function EntryList({
  heading,
  entries,
  untitled,
}: {
  heading: string;
  entries: Array<{ title: string; detail: string }>;
  untitled: string;
}) {
  if (entries.length === 0) return null;
  return (
    <div>
      <h3 className="text-xs font-medium text-[var(--text)] mb-1">{heading}</h3>
      <ul
        className="text-xs text-[var(--text-muted)] max-h-40 overflow-y-auto rounded-md px-3 py-2 space-y-0.5"
        style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}
      >
        {entries.map((entry, index) => (
          <li key={index} className="break-words">
            <span className="text-[var(--text)]">{entry.title || untitled}</span>
            {' — '}
            {entry.detail}
          </li>
        ))}
      </ul>
    </div>
  );
}
